/**
 * Utility to extract the correct user ID from JWT token
 * Fixes the "mgmid in the token does not match the request" error
 */

/**
 * Decodes JWT token and extracts the MGM ID from various possible claims
 * @param token - JWT token string
 * @returns The MGM ID value or null if not found
 */
export function extractMgmIdFromToken(token: string): string | null {
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format');
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];

    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);

    // Decode base64
    const decodedPayload = atob(paddedPayload);

    // Parse JSON
    const claims = JSON.parse(decodedPayload);

    // Log all claims for debugging
    console.log('🔍 JWT Token Claims:', {
      allClaims: Object.keys(claims),
      possibleMgmIds: {
        'com.mgm.id': claims['com.mgm.id'],
        mlife: claims['mlife'],
        mlifeNumber: claims['mlifeNumber'],
        mgmId: claims['mgmId'],
        sub: claims['sub'],
        uid: claims['uid'],
      },
    });

    // Try multiple possible claim names for MGM ID
    const possibleClaimNames = ['com.mgm.id', 'mlife', 'mlifeNumber', 'mgmId', 'uid'];

    for (const claimName of possibleClaimNames) {
      const mgmId = claims[claimName];
      if (mgmId && typeof mgmId === 'string' && mgmId.trim() !== '') {
        console.log(`✅ Found MGM ID in claim '${claimName}':`, mgmId);
        return mgmId;
      }
    }

    console.warn('❌ No valid MGM ID claim found in token');
    return null;
  } catch (error) {
    console.error('Error extracting mgmId from token:', error);
    return null;
  }
}

/**
 * Gets the user ID (mgmId) from the current access token
 * @returns Promise<string | null> - The user's MGM ID or null if not available
 */
export async function getUserIdFromToken(): Promise<string | null> {
  try {
    const token = await window.mgm?.authClient?.getAccessToken();

    if (!token?.accessToken) {
      console.warn('No access token available');
      return null;
    }

    return extractMgmIdFromToken(token.accessToken);
  } catch (error) {
    console.error('Error getting user ID from token:', error);
    return null;
  }
}

/**
 * Gets the user ID with fallback to legacy method for backward compatibility
 * @returns Promise<string | null> - The user's MGM ID
 */
export async function getUserIdWithFallback(): Promise<string | null> {
  try {
    // First, try to get from com.mgm.id claim (correct method)
    const mgmIdFromClaim = await getUserIdFromToken();

    if (mgmIdFromClaim) {
      return mgmIdFromClaim;
    }

    // Fallback to legacy method if com.mgm.id is not available
    console.warn('Falling back to legacy user ID extraction method');
    const tokens = await window.mgm?.authClient?.acquireUserTokens();

    if (tokens?.mlife) {
      return tokens.mlife;
    }

    console.error('Could not extract user ID from token using any method');
    return null;
  } catch (error) {
    console.error('Error getting user ID:', error);
    return null;
  }
}
